/**
 * Integrytas Group — LanguageSwitcher (header icon).
 *
 * A globe button placed beside the theme selector in the title bar. Opens a popover
 * listing the app's active locales (csm_language_master) and lets the signed-in user
 * switch their UI language on the spot — applies it live (i18next + DB overrides)
 * and persists it to csm_user_master.preferred_locale so it sticks next login.
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import { apiDb } from '../../lib/api_client';
import { routedUpdate } from '../../lib/routed_writes';
import type { AuthSession } from '../../types/auth';
import { setActiveCsmLocale, loadI18nOverrides, csmLngTag, i18n } from '../../i18n';
import { localeFlag, localeName } from '../../i18n/locale_display';

export function LanguageSwitcher({ session }: { session: AuthSession }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [locales, setLocales] = useState<string[]>([]);
  const [current, setCurrent] = useState<string>(i18n.language || session.effective_user.preferred_locale);
  const ref = useRef<HTMLDivElement>(null);

  // Keep the flag in step with the ACTIVE language, including the programmatic
  // switch the app makes on login from the user's preferred_locale — otherwise the
  // flag stays on whatever i18n.language was when this control first mounted.
  useEffect(() => {
    const onChanged = (lng: string) => setCurrent(lng);
    i18n.on('languageChanged', onChanged);
    setCurrent(i18n.language);
    return () => { i18n.off('languageChanged', onChanged); };
  }, []);

  useEffect(() => {
    void (async () => {
      const { data } = await apiDb
        .from('csm_language_master')
        .select('internal_code')
        .eq('is_active', 'Y')
        .order('internal_code');
      setLocales(((data ?? []) as { internal_code: string }[]).map((r) => r.internal_code));
    })();
  }, []);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const choose = async (code: string) => {
    setActiveCsmLocale(code);
    await loadI18nOverrides(code);
    setCurrent(code);
    try {
      // §2: route through the Common Routine (forensic stamp + audit + hooks).
      await routedUpdate(session, 'csm_user_master', 'user_id', session.effective_user.user_id, {
        preferred_locale: code,
      });
    } catch {
      /* preference persistence is best-effort */
    }
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={t('shell.language', 'Language')}
        title={`${t('shell.language', 'Language')} — ${localeName(current, current)}`}
        className="flex h-7 w-7 items-center justify-center rounded text-white/80 hover:bg-white/10 hover:text-white"
      >
        <span className="text-base leading-none select-none">{localeFlag(current)}</span>
      </button>

      {open && (
        // Sized to its longest language name rather than to a fixed 14rem, which
        // clipped anything past ~24 characters — "British English (SCOTLAND)"
        // read as "British English (SCOTLA…", i.e. the part that distinguishes it
        // from plain British English was the part cut off. min-w keeps the old
        // width as the floor so short lists look unchanged; max-w caps a
        // pathological name and leaves `truncate` below as the last resort.
        <div className="absolute right-0 top-full z-[200] mt-1 w-max min-w-56 max-w-[min(24rem,calc(100vw-1rem))] rounded border border-csm-border bg-csm-surface shadow-xl">
          <div className="px-3 py-2 text-csm-xs font-semibold uppercase tracking-wider text-csm-muted">{t('shell.language', 'Language')}</div>
          <div className="max-h-80 overflow-auto border-t border-csm-border-soft py-1">
            {locales.length === 0 && <div className="px-3 py-1.5 text-csm-xs italic text-csm-watermark">…</div>}
            {locales.map((code) => {
              const active = csmLngTag(current) === csmLngTag(code);
              return (
                <button
                  key={code}
                  role="menuitem"
                  type="button"
                  onClick={() => void choose(code)}
                  className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-csm-sm ${active ? 'bg-primary/5' : 'hover:bg-csm-surface-alt'}`}
                >
                  <span className="flex h-4 w-4 items-center justify-center text-primary">{active && <Check className="h-3.5 w-3.5" />}</span>
                  <span className="text-base leading-none select-none">{localeFlag(code)}</span>
                  <span className="flex-1 truncate text-csm-ink">{localeName(code, current)}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
